<template>
  <div class="condition-boxes">
    <div class="condition-box" :class="[scoreStatusClass, scoreBackgroundClass]">
      <div class="box-header">
        <Icons name="StoreBadge" size="lg" color="#1C1C1E" />
        <span class="box-title">Mağaza puanınız 8 ve üzeri olmalı</span>
      </div>
      <div class="box-content">
        <div class="label">
          <span class="label-text">Mağaza Puanınız:</span>
          <div v-if="storeScore === null" class="value-container value-null">
            <Icons name="NullDash" class="null-icon" color="white" />
          </div>
          <div v-else class="value-container" :class="scoreValueClass">
            <span class="value-text">{{ scoreValue }}</span>
          </div>
        </div>
        <div class="info">
          <span class="info-text">Mağazanın son 3 aydaki yönetim performansı ve müşteri memnuniyeti verilerine göre hesaplanır.</span>
        </div>
      </div>
    </div>

    <div class="condition-box" :class="[paymentStatusClass, paymentBackgroundClass]">
      <div class="box-header">
        <Icons name="WalletPayment" size="lg" color="#1C1C1E" />
        <span class="box-title">En az 4 hak ediş ödemesi almış olmalısınız</span>
      </div>
      <div class="box-content">
        <div class="label">
          <span class="label-text">Geçmiş hak ediş ödemesi sayınız:</span>
          <span class="value">{{ paymentCount }}</span>
        </div>
        <div class="info">
          <span class="info-text">{{ paymentInfoText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { Icons } from '@n11/so-ui-components';

export default {
  name: "ConditionsModalStatuses",
  components: {
    Icons
  },
  props: {
    storeScore: {
      type: [Number, null],
      default: undefined
    },
    paymentCount: {
      type: Number,
      default: undefined
    }
  },
  setup(props) {
    const scoreBackgroundClass = computed(() => {
      if (props.storeScore === null) return 'bg-null';
      if (props.storeScore >= 8) return 'bg-success';
      if (props.storeScore >= 5) return 'bg-warning';
      if (props.storeScore >= 3) return 'bg-orange';
      return 'bg-error';
    });

    const scoreStatusClass = computed(() => {
      if (props.storeScore === null) return 'status-null';
      if (props.storeScore >= 8) return 'status-success';
      if (props.storeScore >= 5) return 'status-warning';
      if (props.storeScore >= 3) return 'status-orange';
      return 'status-error';
    });

    const scoreValue = computed(() => {
      if (props.storeScore === null) return '—';
      return props.storeScore;
    });

    const scoreValueClass = computed(() => {
      if (props.storeScore === null) return 'highlight-null';
      if (props.storeScore >= 8) return 'highlight-success';
      if (props.storeScore >= 5) return 'highlight-warning';
      if (props.storeScore >= 3) return 'highlight-orange';
      return 'highlight-error';
    });

    const paymentBackgroundClass = computed(() => {
      if (props.paymentCount >= 4) return 'bg-success';
      return 'bg-error';
    });

    const paymentStatusClass = computed(() => {
      if (props.paymentCount >= 4) return 'status-success';
      return 'status-error';
    });

    const paymentInfoText = computed(() => {
      if (props.paymentCount >= 4) {
        return 'Erken ödeme talep etmek için yeterli hak ediş ödemesi aldınız.';
      }
      
      const remaining = 4 - props.paymentCount;
      return `Erken ödeme talep edebilmeniz için ${remaining} hak ediş daha kaldı.`;
    });

    return {
      scoreBackgroundClass,
      scoreStatusClass,
      scoreValue,
      scoreValueClass,
      paymentBackgroundClass,
      paymentStatusClass,
      paymentInfoText
    };
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.condition-boxes {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}

.condition-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 12px;
  border-radius: 6px;
  flex: 1;
  min-height: 110px;

  &.bg-null {
    background: $colorN10;
  }

  &.bg-success {
    background: $colorS05;
  }

  &.bg-warning {
    background: $colorY05;
  }

  &.bg-orange {
    background: #FFF0E6;
  }

  &.bg-error {
    background: $colorE05;
  }

  .box-header {
    display: flex;
    align-items: center;
    gap: 4px;

    .box-title {
      color: $colorN80;
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: -0.05px;
    }
  }

  .box-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    align-self: stretch;

    .label-text,
    .info-text {
      color: $colorN80;
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: -0.05px;
    }

    .value {
      color: $colorN80;
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
    }

    .label {
      display: flex;
      padding: 0 28px;
      align-items: center;
      gap: 4px;
      align-self: stretch;
    }

    .info {
      display: flex;
      padding-left: 28px;
      align-items: center;
      gap: 4px;
      align-self: stretch;
    }

    .value-container {
      display: flex;
      padding: 2px 4px;
      align-items: flex-start;
      gap: 8px;
      border-radius: 4px;

      .value-text {
        color: $colorN00;
        font-size: 11px;
        font-weight: 700;
        line-height: 12px;
      }

      &.value-null {
        width: 24px;
        height: 16px;
        padding: 2px 6px;
        justify-content: center;
        align-items: center;
        background: $colorS60;

        .null-icon {
          :deep(svg) {
            width: 7.3px;
            height: 1.7px;
            fill: $colorN00;
          }
        }
      }

      &.highlight-success {
        background: $colorS50;
      }

      &.highlight-warning {
        background: $colorY40;
      }

      &.highlight-orange {
        background: #FF8B3D;
      }

      &.highlight-error {
        background: $colorE50;
      }

      &.highlight-null {
        background: $colorS60;
      }
    }
  }
}
</style>
